import { VirtualBackgroundType } from 'src/app/common/interfaces/rtc-interface';
import { UploadDialogComponent } from 'src/app/dialogs/upload/upload-dialog.component';
import { ResourceItemModel } from 'src/app/models/resource';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { User } from 'src/app/models/user';
import { Observable, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filterNil } from '@ngneat/elf';
import { VirtualBackgroundInsertableStreamService } from '../virtual-background-insertable-stream.service';
import { UserService } from '../user.service';
import { ResourcesService } from '../resources.service';
import { UploadFileService } from '../upload-file.service';
import { Libraries } from '../../dialogs/upload/upload/upload.component';
import { FlagsService, FLAGS } from '../../services/flags.service';
import { SpaceRepository, ISpaceUI } from '../../state/space.repository';
import { ISession } from '../../models/session';
import { VirtualBackgroundUtilsService } from './virtual-background-utils.service';

export interface VBImage {
  src: string;
  thumbnail?: string;
  onClick: (vbClickEvent: VBClickEvent) => void;
  active?: boolean;
  title?: string;
}

enum CategoryVisibility {
  ME = 'ME',
  CLASS = 'CLASS',
}

export interface VBImageCategory {
  title: string;
  images: VBImage[];
  isDeletable: boolean;
  maxNumberOfImages: number;
  folderId: string;
  userUploaded?: boolean;
  hasImageLabels?: boolean;
  hasUpload?: boolean;
}

export interface VBClickEvent {
  image: VBImage;
  category: VBImageCategory;
}

export const ALLOWED_IMAGE_TYPES = 'image/jpeg, image/jpg, image/png, image/webp';

@UntilDestroy()
export abstract class VirtualBackgroundPanelBase {
  private readonly MAX_CUSTOM_IMAGE_SIZE_IN_MB = 3;
  private static readonly MAX_NUMBER_OF_IMAGES_PER_CATEGORY = 20;
  private vBSubscribers: Subscription[] = [];
  private user?: User;
  private activeSpace?: ISession & ISpaceUI;
  private readonly CUSTOM_BACKGROUND_PREFIX = 'Custom';

  public vbImagesCategories: VBImageCategory[] = [];

  private readonly VB_IMAGES_KEY = 'VB_PRIVATE_UPLOADED_IMAGES_KEY';
  private readonly PRIVATE_VIRTUAL_BACKGROUND_TITLE = this.translate
    .instant('other backgrounds')
    .toUpperCase();
  private readonly DEFAULT_VIRTUAL_BACKGROUND_TITLE = this.translate
    .instant('Default backgrounds')
    .toUpperCase();

  protected _lastVBImage?: VBImage;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private uploadFileService: UploadFileService,
    private resourcesService: ResourcesService,
    private userService: UserService,
    protected virtualBackgroundInsertableStreamService: VirtualBackgroundInsertableStreamService,
    protected flagsService: FlagsService,
    private vbUtilsService: VirtualBackgroundUtilsService,
    private spaceRepo: SpaceRepository,
  ) {}

  private async initializeBackgrounds(): Promise<void> {
    const institution = this.activeSpace?.institution;
    const isRestricted = this.vbUtilsService.isInstitutionVBsRestricted(institution);
    this.setupVBInstitutionCategory();

    // Setup other categories only if not restricted
    if (!isRestricted) {
      if (this.flagsService.isFlagEnabled(FLAGS.DEFAULT_VIRTUAL_BACKGROUNDS)) {
        this.setupDefaultBackgrounds();
      }
      this.setupVBPrivateCategory();
    }
  }

  private setupDefaultBackgrounds(): void {
    const preloadedBackgrounds =
      this.flagsService.featureFlagsVariables[FLAGS.DEFAULT_VIRTUAL_BACKGROUNDS]?.[
        'preloaded_backgrounds'
      ];

    if (!preloadedBackgrounds || typeof preloadedBackgrounds !== 'object') {
      return;
    }

    const defaultCategory: VBImageCategory = {
      title: this.DEFAULT_VIRTUAL_BACKGROUND_TITLE,
      images: [],
      isDeletable: false,
      maxNumberOfImages: VirtualBackgroundPanelBase.MAX_NUMBER_OF_IMAGES_PER_CATEGORY,
      folderId: '', // No folder needed for default backgrounds
      userUploaded: false,
      hasImageLabels: true,
      hasUpload: false,
    };

    // Parse the preloaded backgrounds object
    try {
      Object.entries(preloadedBackgrounds).forEach(([title, value]: [string, any]) => {
        if (value && value.src) {
          defaultCategory.images.push(this.createVbImage(value.src, title, value.thumbnail));
        }
      });

      if (defaultCategory.images.length > 0) {
        this.vbImagesCategories.push(defaultCategory);
      }
    } catch (e) {
      console.error('Error parsing preloaded backgrounds:', e);
    }
  }

  ngOnDestroy(): void {
    this.clearVBSubscribers();
  }

  get virtualBackgroundType(): VirtualBackgroundType {
    return this.virtualBackgroundInsertableStreamService.virtualBackgroundType;
  }

  async setupVBInstitutionCategory(): Promise<void> {
    const institution = this.activeSpace?.institution;
    const folderId = this.vbUtilsService.getInstitutionVBsFolder(institution);
    if (!folderId || !this.vbUtilsService.hasInstitutionVBsImages(institution)) {
      return;
    }

    this.vbImagesCategories.push({
      title: this.translate.instant('FROM YOUR INSTITUTION'),
      images: [],
      isDeletable: false,
      maxNumberOfImages: VirtualBackgroundPanelBase.MAX_NUMBER_OF_IMAGES_PER_CATEGORY,
      folderId: folderId,
      hasImageLabels: false,
      hasUpload: false,
    });
    this.loadCategoryImages(this.vbImagesCategories[this.vbImagesCategories.length - 1]);
  }

  subscribeCurrUserAdminInstitutionChange() {
    const userSub = this.userService.user.subscribe((userData) => {
      if (userData?.user) {
        this.user = userData.user;
      }
    });
    this.vBSubscribers.push(userSub);

    this.spaceRepo.activeSpaceId$
      .pipe(filterNil(), untilDestroyed(this))
      .subscribe((activeSpaceId) => {
        this.activeSpace = this.spaceRepo.getSpace(activeSpaceId);
        this.initializeBackgrounds();
      });
  }

  async setupVBPrivateCategory(): Promise<void> {
    if (this.user?.virtualBackgroundsFolder === null) {
      const resourcesSub = this.resourcesService
        .createFolder({
          name: 'Your Virtual Backgrounds',
          acl: {
            visibility: CategoryVisibility.ME,
            public: false,
          },
        })
        .subscribe((created) => {
          if (created && created.body && this.user) {
            this.user.virtualBackgroundsFolder = created.body._id;
            this.userService.updateUser(this.user);
          }
        });
      this.vBSubscribers.push(resourcesSub);
    }

    const privateCategory: VBImageCategory = {
      title: this.PRIVATE_VIRTUAL_BACKGROUND_TITLE,
      images: [],
      isDeletable: false,
      maxNumberOfImages: VirtualBackgroundPanelBase.MAX_NUMBER_OF_IMAGES_PER_CATEGORY,
      folderId: this.user?.virtualBackgroundsFolder ?? '',
      userUploaded: true,
      hasImageLabels: true,
      hasUpload: true,
    };

    this.vbImagesCategories.push(privateCategory);
    this.loadLocalStorageImages(privateCategory);
  }

  deleteImage(category: VBImageCategory, src: string) {
    // Only allow deleting images from user uploaded categories
    if (!category.userUploaded) {
      return;
    }

    category.images = category.images.filter((img) => img.src != src);

    if (this.isPrivateCategory(category)) {
      this.removeImageFromLocalStorage(src);
    }
  }

  private loadLocalStorageImages(category: VBImageCategory) {
    const list: string[] = JSON.parse(localStorage.getItem(this.VB_IMAGES_KEY) || '[]');
    list.forEach((img, index) => {
      const title = `${this.CUSTOM_BACKGROUND_PREFIX} ${index + 1}`;
      category.images.push(this.createVbImage(img, title));
    });
  }

  private removeImageFromLocalStorage(src: string) {
    let list: string[] = JSON.parse(localStorage.getItem(this.VB_IMAGES_KEY) || '[]');
    list = list.filter((it) => it != src);
    localStorage.setItem(this.VB_IMAGES_KEY, JSON.stringify(list));
  }

  private isImageSelected(src: string) {
    return (
      this.virtualBackgroundInsertableStreamService.isVirtualBackgroundMode() &&
      this.virtualBackgroundType == VirtualBackgroundType.BACKGROUND_IMAGE &&
      this.virtualBackgroundInsertableStreamService.lastBackgroundImageSource === src
    );
  }

  private loadCategoryImages(category: VBImageCategory): void {
    const resourcesSub = this.resourcesService
      .getPublicGuestFolder(category.folderId)
      .subscribe((folder) => {
        const resources = folder.resources?.files ?? [];
        resources.forEach((resource) => {
          if (resource.url) {
            category.images.push(this.createVbImage(resource.url));
          }
        });
      });
    this.vBSubscribers.push(resourcesSub);
  }

  private isPrivateCategory(category: VBImageCategory) {
    return category.title === this.PRIVATE_VIRTUAL_BACKGROUND_TITLE;
  }

  private addImageSrcToLocalStorage(src: string) {
    const list: string[] = JSON.parse(localStorage.getItem(this.VB_IMAGES_KEY) || '[]');
    list.push(src);
    localStorage.setItem(this.VB_IMAGES_KEY, JSON.stringify(list));
  }

  private getNextCustomBackgroundIndex(category: VBImageCategory): number {
    // Find the highest Custom index currently in use
    let maxIndex = 0;

    category.images.forEach((image) => {
      if (image.title && image.title.startsWith(this.CUSTOM_BACKGROUND_PREFIX)) {
        const indexStr = image.title.substring(this.CUSTOM_BACKGROUND_PREFIX.length).trim();
        const index = parseInt(indexStr, 10);
        if (!isNaN(index) && index > maxIndex) {
          maxIndex = index;
        }
      }
    });

    return maxIndex + 1;
  }

  // Public method for uploading backgrounds directly from UI
  uploadBackground(): void {
    // Find the appropriate target category for uploads
    const targetCategory =
      this.vbImagesCategories.find((category) => category.userUploaded) ||
      this.vbImagesCategories.find((category) => this.isPrivateCategory(category));

    if (!targetCategory) {
      return; // No suitable category found for upload
    }

    const uploadDialogRef = this.dialog.open(UploadDialogComponent, {
      width: '80%',
      height: '80%',
      panelClass: 'upload-dialog-container',
      data: {
        title: this.translate.instant('Upload Virtual Background'),
        libraries: [Libraries.COMPUTER],
        type: [ResourceItemModel.IMAGE],
        allowedImageTypes: ALLOWED_IMAGE_TYPES,
        maxFileSizeInMB: this.MAX_CUSTOM_IMAGE_SIZE_IN_MB,
        forceLegacyUpload: true,
      },
    });

    const uploadSub = uploadDialogRef.afterClosed().subscribe((result) => {
      if (result?.files?.[0]?.file) {
        const file = result.files[0].file;
        this.uploadFileService.uploadToFireStorage(
          file,
          (url) => {
            if (this.isPrivateCategory(targetCategory)) {
              this.addImageSrcToLocalStorage(url);
            }

            // Generate a title for the new background
            const nextIndex = this.getNextCustomBackgroundIndex(targetCategory);
            const title = `${this.CUSTOM_BACKGROUND_PREFIX} ${nextIndex}`;

            targetCategory.images.push(this.createVbImage(url, title));
            this.resourcesService.createResource({
              url: url,
              name: file.name,
              size: file.size,
              acl: {
                visibility: this.isPrivateCategory(targetCategory)
                  ? CategoryVisibility.ME
                  : CategoryVisibility.CLASS,
              },
              type: ResourceItemModel.IMAGE,
              metadata: {},
              folder: targetCategory.folderId,
            });
          },
          () => {},
          true,
          this.MAX_CUSTOM_IMAGE_SIZE_IN_MB,
        );
      }
    });
    this.vBSubscribers.push(uploadSub);
  }

  private createVbImage(src: string, title?: string, thumbnail?: string) {
    const vbImage = {
      src: src,
      thumbnail: thumbnail,
      onClick: (vbClickEvent: VBClickEvent) => {
        this.handleChangingChoosenImageActiveStatus(vbImage);
        this.setImageBackground(vbClickEvent);
      },
      active: this.isImageSelected(src),
      title: title || '',
    };
    if (vbImage.active) {
      this._lastVBImage = vbImage;
    }
    return vbImage;
  }

  private clearVBSubscribers() {
    this.vBSubscribers.forEach((subscriber) => subscriber.unsubscribe());
  }

  private handleChangingChoosenImageActiveStatus(vbImage: VBImage) {
    if (this._lastVBImage) {
      this._lastVBImage.active = false;
    }

    vbImage.active = true;
    this._lastVBImage = vbImage;
  }

  abstract setImageBackground(vbClickEvent: VBClickEvent): Promise<void>;

  abstract removeBackground(): Promise<void>;

  abstract blur(): Promise<void>;

  abstract getVideoStream(): Observable<MediaStream | null>;

  abstract isVideoPlaying(): Observable<boolean>;
}
